import RootStore from '@App/stores/root-store'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { AxiosResponse } from 'axios'
import { updateWorkMergeVote } from '@App/api/routes/rightsholder/rightsholder'
import { WorkMergeRequestDetail, WorkMergeVote } from '@Entities/rightsholder/work-merge'

type WorkMergeRequestGetFunction = (
    rightsholderId: number,
    workMergeRequestId: number
) => Promise<AxiosResponse<WorkMergeRequestDetail>>

export default class WorkMergeRequestDetailStore {
    private rootStore: RootStore

    private readonly workMergeRequestGetFunction: WorkMergeRequestGetFunction

    constructor(rootStore: RootStore, workMergeRequestGetFunction: WorkMergeRequestGetFunction) {
        makeObservable<WorkMergeRequestDetailStore, '_currentRequest' | '_isLoading' | '_isSavingVote'>(this, {
            _currentRequest: observable,
            _isLoading: observable,
            _isSavingVote: observable,
            clearCurrentRequest: action,
        })
        this.rootStore = rootStore
        this.workMergeRequestGetFunction = workMergeRequestGetFunction
    }

    private _currentRequest: WorkMergeRequestDetail | undefined

    private _isLoading = true

    private _isSavingVote = false

    get currentRequest(): WorkMergeRequestDetail | undefined {
        return this._currentRequest
    }

    get isLoading(): boolean {
        return this._isLoading
    }

    get isSavingVote(): boolean {
        return this._isSavingVote
    }

    async loadRequest(workMergeRequestId: number): Promise<void> {
        runInAction(() => {
            this._isLoading = true
        })

        try {
            const result = await this.workMergeRequestGetFunction(
                this.rootStore.rightsholderStore.currentRightsholderId,
                workMergeRequestId
            )
            runInAction(() => {
                this._currentRequest = result.data
            })
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        } finally {
            runInAction(() => {
                this._isLoading = false
            })
        }
    }

    async voteOnRequest(vote: WorkMergeVote): Promise<boolean> {
        const request = this._currentRequest
        if (!request) {
            return false
        }

        const rightsholderId = this.rootStore.rightsholderStore.currentRightsholderId
        const votableWorks = [request.primaryWork, ...request.secondaryWorks].filter((requestWork) =>
            requestWork.votes?.some((memberVote) => memberVote.ipId === rightsholderId)
        )
        if (votableWorks.length === 0) {
            return false
        }

        runInAction(() => {
            this._isSavingVote = true
        })
        this.rootStore.preloaderStore.openTransparentPreloader(true)

        try {
            await Promise.all(
                votableWorks.map((requestWork) =>
                    updateWorkMergeVote(rightsholderId, request.id, requestWork.workId, { vote })
                )
            )
            return true
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
            return false
        } finally {
            this.rootStore.preloaderStore.openTransparentPreloader(false)
            runInAction(() => {
                this._isSavingVote = false
            })
        }
    }

    clearCurrentRequest(): void {
        this._currentRequest = undefined
        this._isLoading = true
    }
}
